import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import BottomSheet from '../common/BottomSheet';
import { getChordLibrary } from '../../utils/chordLibrary';
import { chordEm } from '../../utils/chordGlyphs';


import './ChordLibrarySheet.scss';

// Past a triad's width a name steps down a size rather than running into the button's edges
const sizeClass = (name) => {
  const em = chordEm(name);

  if (em > 4.2) return 'chord-name--longer';
  if (em > 3.4) return 'chord-name--long';

  return null;
};

/**
 * A slide-up sheet of every chord that works in the song's key: its own chords, the ones borrowed
 * from the parallel scale and the modes on the same tonic, slash chords, and 7ths, sus and add9.
 *
 * A tap plays the chord and holds it down; Add writes the held one at the builder's cursor. A
 * borrowed chord that is also one of the key's own carries the key's colour and dot, so what a mode
 * actually adds is what stands out in its row. New only goes further and drops every repeat, so
 * each row holds nothing but the chords it is the first to bring.
 */
function ChordLibrarySheet({ songKey, isOpen, onClose, onPlay, onAdd }) {
  const { t, i18n } = useTranslation();
  const library = useMemo(
    () => getChordLibrary(songKey),
    // The rows are named in the app's language
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [songKey, i18n.language],
  );
  const [selected, setSelected] = useState(null);
  const [pressing, setPressing] = useState(null);
  const [hidden, setHidden] = useState(() => new Set());
  const [newOnly, setNewOnly] = useState(false);

  useEffect(() => {
    if (isOpen) setSelected(null);
  }, [isOpen]);

  // The rows as listed: chips tapped off drop out, and under New only a row keeps just the chords
  // no row above it already showed, so what is left is what that scale alone brings
  const scales = useMemo(() => {
    const shown = library ? library.scales.filter((scale) => !hidden.has(scale.id)) : [];

    if (!newOnly) return shown;

    const seen = new Set(library.common.map((chord) => chord.name));

    return shown
      .map((scale) => {
        const chords = scale.chords.filter((chord) => !seen.has(chord.name));

        chords.forEach((chord) => seen.add(chord.name));

        return { ...scale, chords };
      })
      .filter((scale) => scale.chords.length > 0);
  }, [library, hidden, newOnly]);

  if (!library) return null;

  const toggleScale = (id) =>
    setHidden((current) => {
      const next = new Set(current);

      if (next.has(id)) next.delete(id);
      else next.add(id);

      return next;
    });

  const pick = (name) => {
    setSelected(name);
    onPlay(name);
  };

  const renderChord = ({ name, roman }, className) => (
    <button
      key={name}
      type="button"
      className={cx('chord', className, { 'is-pressed': selected === name || pressing === name })}
      // Held down from the touch rather than by :active, which lets go before the pick lands and
      // bounces the key. Picked on release, not on press as the piano does — a finger here may be
      // scrolling, and the scroll cancels the press
      onPointerDown={(e) => e.button === 0 && setPressing(name)}
      onPointerUp={(e) => {
        if (pressing === name) pick(name);
        setPressing(null);
      }}
      onPointerCancel={() => setPressing(null)}
      // A touch stays on the key it started on; a mouse dragged off lets go of it
      onPointerLeave={(e) => e.pointerType === 'mouse' && setPressing(null)}
      // Keyboard activation (Enter / Space) is the only click with no pointer press
      onClick={(e) => e.detail === 0 && pick(name)}
    >
      <span className={cx('chord-name', sizeClass(name))}>{name}</span>
      {roman && <span className="roman">{roman}</span>}
    </button>
  );

  const footer = (
    <>
      <span className={cx('selection', { 'selection--empty': !selected })}>
        {selected ?? t('chordLibrary.tapToHear')}
      </span>
      <button type="button" className="add" disabled={!selected} onClick={() => onAdd(selected)}>
        {t('chordLibrary.add')}
      </button>
    </>
  );

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={t('dialogs.chordBars.chordLibrary')}
      className="ChordLibrarySheet"
      footer={footer}
    >
      <div className="view-tabs" role="group" aria-label={t('chordLibrary.view')}>
        {[
          [false, t('chordLibrary.allChords')],
          [true, t('chordLibrary.newOnly')],
        ].map(([value, label]) => (
          <button
            key={label}
            type="button"
            className={cx('tab', { active: newOnly === value })}
            aria-pressed={newOnly === value}
            onClick={() => setNewOnly(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="legend">
        <span className="legend-item legend-item--key">{t('chordLibrary.inKey')}</span>
        {library.scales.map((scale) => (
          <button
            key={scale.id}
            type="button"
            className={cx('legend-item', `legend-item--${scale.id}`, {
              'legend-item--off': hidden.has(scale.id),
            })}
            aria-pressed={!hidden.has(scale.id)}
            onClick={() => toggleScale(scale.id)}
          >
            {scale.label}
          </button>
        ))}
        <span className="legend-item legend-item--chromatic">{t('chordLibrary.chromatic')}</span>
      </div>

      <section className="group group--key">
        <h4 className="group-title">
          {t('chordLibrary.keyOf', { key: songKey })}
          <span className="group-note">{t('chordLibrary.commonChords')}</span>
        </h4>
        <div className="chords chords--scale">
          {library.common.map((chord) => renderChord(chord, 'chord--key'))}
        </div>
      </section>

      {scales.map((scale) => (
        <section key={scale.id} className={`group group--${scale.id}`}>
          <h4 className="group-title">
            {scale.label}
            <span className="group-note">{scale.title}</span>
          </h4>
          <div className="chords chords--scale">
            {scale.chords.map((chord) => renderChord(chord, chord.isCommon ? 'chord--key' : 'chord--borrowed'))}
          </div>
        </section>
      ))}

      {library.passing.length > 0 && (
        <section className="group">
          <h4 className="group-title">
            {t('chordLibrary.passing')}
            <span className="group-note">{t('chordLibrary.passingNote')}</span>
          </h4>
          <div className="chords">
            {library.passing.map((chord) => renderChord(chord, 'chord--borrowed chord-family--chromatic'))}
          </div>
        </section>
      )}

      {library.slash.length > 0 && (
        <section className="group">
          <h4 className="group-title">{t('chordLibrary.slash')}</h4>
          <div className="chords">
            {library.slash.map((chord) => renderChord(chord, 'chord-family--key'))}
          </div>
        </section>
      )}

      {library.extensions
        .filter((group) => group.chords.length > 0)
        .map((group) => (
          <section key={group.id} className="group">
            <h4 className="group-title">{group.label}</h4>
            <div className="chords">
              {group.chords.map((chord) => renderChord(chord, 'chord-family--key'))}
            </div>
          </section>
        ))}
    </BottomSheet>
  );
}

ChordLibrarySheet.propTypes = {
  songKey: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPlay: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

export default ChordLibrarySheet;
